import classNames from 'classnames';

import { Skeleton, SkeletonProps } from '../../skeleton';
import {
    isGrid2xlBreakpoint,
    isGridBreakpoint,
    isGridDefBreakpoint,
    isGridLgBreakpoint,
    isGridMdBreakpoint,
    isGridSmBreakpoint,
    isGridXlBreakpoint
} from '../grid-utils';
import { GridBreakpoints, GridClassName, GridSpan } from '../grid.types';

export const GRID_COL_SPAN_LOOKUP: { [key in GridSpan]: GridClassName } = {
  auto: 'col-span-auto',
  full: 'col-span-full',
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};

export const GRID_COL_SM_SPAN_LOOKUP: { [key in GridSpan]: GridClassName } = {
  auto: 'sm:col-span-auto',
  full: 'sm:col-span-full',
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  5: 'sm:col-span-5',
  6: 'sm:col-span-6',
  7: 'sm:col-span-7',
  8: 'sm:col-span-8',
  9: 'sm:col-span-9',
  10: 'sm:col-span-10',
  11: 'sm:col-span-11',
  12: 'sm:col-span-12',
};

export const GRID_COL_MD_SPAN_LOOKUP: { [key in GridSpan]: GridClassName } = {
  auto: 'md:col-span-auto',
  full: 'md:col-span-full',
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

export const GRID_COL_LG_SPAN_LOOKUP: { [key in GridSpan]: GridClassName } = {
  auto: 'lg:col-span-auto',
  full: 'lg:col-span-full',
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
};

export const GRID_COL_XL_SPAN_LOOKUP: { [key in GridSpan]: GridClassName } = {
  auto: 'xl:col-span-auto',
  full: 'xl:col-span-full',
  1: 'xl:col-span-1',
  2: 'xl:col-span-2',
  3: 'xl:col-span-3',
  4: 'xl:col-span-4',
  5: 'xl:col-span-5',
  6: 'xl:col-span-6',
  7: 'xl:col-span-7',
  8: 'xl:col-span-8',
  9: 'xl:col-span-9',
  10: 'xl:col-span-10',
  11: 'xl:col-span-11',
  12: 'xl:col-span-12',
};

export const GRID_COL_2XL_SPAN_LOOKUP: { [key in GridSpan]: GridClassName } = {
  auto: '2xl:col-span-auto',
  full: '2xl:col-span-full',
  1: '2xl:col-span-1',
  2: '2xl:col-span-2',
  3: '2xl:col-span-3',
  4: '2xl:col-span-4',
  5: '2xl:col-span-5',
  6: '2xl:col-span-6',
  7: '2xl:col-span-7',
  8: '2xl:col-span-8',
  9: '2xl:col-span-9',
  10: '2xl:col-span-10',
  11: '2xl:col-span-11',
  12: '2xl:col-span-12',
};

export interface GridColProps {
  className?: string;
  span?: GridSpan | GridBreakpoints<GridSpan>;
  title?: string;
  loading?: boolean;
  SkeletonProps?: SkeletonProps;
}

export function GridCol(props: React.PropsWithChildren<GridColProps>) {
  const { className, span, title, loading = false, SkeletonProps } = props;

  return (
    <li
      className={classNames(
        // col-span-*
        !isGridBreakpoint(span) && span ? GRID_COL_SPAN_LOOKUP[span] : undefined,
        isGridDefBreakpoint(span) ? GRID_COL_SPAN_LOOKUP[span.def] : undefined,
        isGridSmBreakpoint(span) ? GRID_COL_SM_SPAN_LOOKUP[span.sm] : undefined,
        isGridMdBreakpoint(span) ? GRID_COL_MD_SPAN_LOOKUP[span.md] : undefined,
        isGridLgBreakpoint(span) ? GRID_COL_LG_SPAN_LOOKUP[span.lg] : undefined,
        isGridXlBreakpoint(span) ? GRID_COL_XL_SPAN_LOOKUP[span.xl] : undefined,
        isGrid2xlBreakpoint(span) ? GRID_COL_2XL_SPAN_LOOKUP[span['2xl']] : undefined,

        className
      )}
      title={title}
    >
      {loading && <Skeleton variant="text" {...SkeletonProps} />}
      {!loading && props.children}
    </li>
  );
}

export default GridCol;
